import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useFetcher, useLocation, useNavigate } from '@remix-run/react';

import Text from '~/components/Text';
import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';
import type { PublicationForm } from '~/interfaces/form';
import type { Publication } from '~/interfaces/publication';

import FormBody from './FormBody';
import FormHeader from './FormHeader';
import FormSubmissionMessage from './FormSubmissionMessage';
import PreviewBanner from './PreviewBanner';
import PreviewHelperNote from './PreviewHelperNote';
import SubscribePrompt from './SubscribePrompt';

interface Props {
  email: string | undefined;
  publication: Publication;
  form: PublicationForm;
  isPreview: boolean;
  disablePreviewNoteCookieExists: boolean;
}

const FormLayout = ({ publication, form, email, isPreview, disablePreviewNoteCookieExists }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const fetcher = useFetcher<{
    redirect?: boolean;
    status?: number;
    toast?: {
      status?: string;
    };
  }>();
  const fetcherData = fetcher.data;
  const isFetcherSubmitting = fetcher.state === 'submitting';
  const { t } = useTranslation();

  const { nextPage } = useSignupRedirects(publication);

  useEffect(() => {
    if (fetcherData?.redirect) {
      navigate('/');
    }

    if (fetcherData?.toast?.status === 'success') {
      setIsSubmitted(true);
    }

    if (fetcherData?.status === 404) {
      navigate(location.pathname, { replace: !!fetcherData?.redirect });
    }
  }, [fetcherData?.toast, isSubmitted]);

  const handleFakeSubmit = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 200);
  };

  if (isSubmitted) {
    return (
      <div className="mx-auto w-full max-w-xl pt-12">
        <FormSubmissionMessage
          message={form.thank_you_message || 'Thank you for your submission.'}
          logo={publication.logo.thumb.url}
          logoAlt={publication.name}
        >
          <button type="button" onClick={nextPage}>
            <div className="flex items-center justify-center space-x-1">
              <Text
                as="span"
                theme="default"
                type="body"
                className="font-regular flex w-full cursor-pointer items-center text-sm text-wt-primary opacity-75 hover:opacity-100"
              >
                {t('shared.cta.continue')} <ChevronRightIcon className="ml-2 h-4 w-4" />
              </Text>
            </div>
          </button>
        </FormSubmissionMessage>
      </div>
    );
  }

  if (!email && !isPreview) {
    return (
      <div className="mx-auto w-full max-w-xl space-y-8 pt-12">
        <FormHeader name={publication.name} title={form.title} description={form.description} />
        <SubscribePrompt />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-xl pt-12">
      {isPreview && <PreviewBanner />}
      {isPreview && !disablePreviewNoteCookieExists && <PreviewHelperNote />}
      {isPreview ? (
        <FormBody
          form={form}
          publication={publication}
          email={email || ''}
          isSubmitting={isSubmitting}
          isPreview={isPreview}
          onPreviewSubmit={handleFakeSubmit}
        />
      ) : (
        <div className="relative">
          <div className="absolute right-0 top-0">
            <button type="button" onClick={nextPage}>
              <div className="flex items-center justify-center">
                <XMarkIcon className="h-6 w-6 text-gray-400 transition-all hover:text-gray-700" />
              </div>
            </button>
          </div>
          <fetcher.Form method="post" action="/post_subscribe_form">
            <FormBody
              form={form}
              publication={publication}
              email={email || ''}
              isSubmitting={isFetcherSubmitting}
              isPreview={isPreview}
            />
          </fetcher.Form>
        </div>
      )}
    </div>
  );
};

export default FormLayout;
